import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { LEGAL, PROJECT_STATUS_STATEMENT, PROJECT_STATUS_TRANSLATIONS } from '../data/legalConstants';
import { useLocale } from '../i18n/LocaleProvider';

export const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-3 font-outfit">{children}</p>
);

export const H2 = ({ children }) => (
  <h2 className="font-outfit font-bold text-xl text-white mb-3 mt-10 first:mt-0">{children}</h2>
);

export const H3 = ({ children }) => (
  <h3 className="font-outfit font-semibold text-white text-base mb-2 mt-6">{children}</h3>
);

export const P = ({ children }) => (
  <p className="text-white/55 text-sm leading-relaxed mb-4">{children}</p>
);

export const Li = ({ children }) => (
  <li className="flex items-start gap-2 mb-2">
    <div className="w-1.5 h-1.5 rounded-full bg-purple-400/60 mt-1.5 flex-shrink-0" />
    <span className="text-white/55 text-sm leading-relaxed">{children}</span>
  </li>
);

export const Value = ({ children }) => (
  <strong className="text-white/85 font-medium">{children}</strong>
);

export const LegalNote = ({ title, children, tone = 'neutral' }) => {
  const toneClasses = {
    neutral: 'bg-white/[0.035] border-white/10 text-white/58',
    safety: 'bg-emerald-500/[0.055] border-emerald-400/15 text-emerald-50/70',
    warning: 'bg-amber-500/[0.055] border-amber-400/15 text-amber-50/75',
  };

  return (
    <div className={`rounded-2xl border p-4 mb-5 ${toneClasses[tone] || toneClasses.neutral}`}>
      {title && <p className="font-outfit font-semibold text-white/85 text-sm mb-2">{title}</p>}
      <div className="text-xs sm:text-sm leading-relaxed">{children}</div>
    </div>
  );
};

const legalLinks = [
  { to: '/privacy', key: 'privacy' },
  { to: '/terms', key: 'terms' },
  { to: '/user-content-copyright', key: 'copyright' },
  { to: '/community-guidelines', key: 'community' },
  { to: '/child-safety-policy', key: 'childSafetyModeration' },
  { to: '/report-handling', key: 'reportHandling' },
  { to: '/data-retention', key: 'dataRetention' },
  { to: '/delete-account', key: 'deleteAccount' },
  { to: '/child-safety', key: 'childSafetyOverview' },
  { to: '/safety-research', key: 'childSafetyResearch' },
];

const LegalLayout = ({ title, eyebrow, children, includeStatusStatement = true, lastUpdated = LEGAL.lastUpdated }) => {
  const { t, locale } = useLocale();
  // Preserve the published source date while displaying it in the reader's language.
  const formattedLastUpdated = lastUpdated === 'October 21, 2026'
    ? new Intl.DateTimeFormat(
        { en: 'en-US', ru: 'ru-RU', es: 'es-ES', fr: 'fr-FR', ko: 'ko-KR', ar: 'ar-AE' }[locale] || 'en-US',
        { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }
      ).format(new Date('2026-10-21T12:00:00Z'))
    : lastUpdated;

  return (
    <main className="bg-[#05050A] min-h-screen">
      <section className="relative pt-32 pb-8 overflow-hidden">
        <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-purple-600/10 rounded-full blur-[100px] pointer-events-none" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="legal-back" className="inline-flex items-center gap-1.5 text-xs text-white/45 hover:text-white transition-colors mb-6">
            <ArrowLeft size={12} /> {t('legal.back')}
          </Link>
          <SectionLabel>{eyebrow || t('legal.eyebrow')}</SectionLabel>
          <h1 className="font-outfit font-black text-[clamp(1.5rem,6.5vw,3rem)] sm:text-5xl leading-tight text-white mb-4 tracking-tight break-words [overflow-wrap:anywhere]">{title}</h1>
          <p className="text-white/40 text-sm">{t('legal.lastUpdated', { date: formattedLastUpdated })}</p>
        </div>
      </section>

      <section className="py-12 pb-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-card p-6 sm:p-10">
            {includeStatusStatement && (
              <div data-testid="legal-status-note" className="p-4 rounded-xl bg-white/[0.035] border border-white/10 mb-8">
                <div className="flex items-start gap-2.5">
                  <FileText size={13} className="text-white/45 mt-0.5 flex-shrink-0" />
                  <p className="text-white/55 text-xs leading-relaxed">
                    <strong className="text-white/80">{t('legal.operatorInfo')}</strong> {PROJECT_STATUS_TRANSLATIONS[locale] || PROJECT_STATUS_STATEMENT}
                  </p>
                </div>
              </div>
            )}

            {children}

            <div className="mt-12 pt-6 border-t border-white/8">
              <p className="text-[10px] font-outfit font-semibold text-white/40 tracking-[0.18em] uppercase mb-3">{t('legal.otherDocs')}</p>
              <div className="flex flex-wrap gap-2">
                {legalLinks.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`legal-link-${l.to.replace('/', '')}`}
                    className="text-[11px] px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/65 hover:text-white hover:border-purple-400/40 hover:bg-purple-500/10 transition-all"
                  >
                    {t(`footer.${l.key}`)}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default LegalLayout;
